import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { addCommasToNumber, GraviButton, GraviGrid, Horizontal, Texto } from '@gravitate-js/excalibrr'
import { useOrderMovements } from '@api/useOrderMovements'
import { formatters } from '@utils/intl/dateTimeFormatters'
import moment from 'moment'
import { addDays, subDays } from 'date-fns'
import { ActionButtons } from '@components/OrderMovements/SiteBOLs/ActionButtons'
import { BookOutlined, FileTextOutlined, WarningFilled } from '@ant-design/icons'
import { Tooltip } from 'antd'
import { defaultDateDef, defaultNumberDef } from '@components/_SiteWide/CellTemplates/typeColumnDefs'
import { useLocalStorage } from '@gravitate-js/the-armory'
import { useSecurityContext } from '@components/Navigation/SecurityContext'
import { Link } from 'react-router-dom'
import { OrderModal } from '@components/OrderWizard/OrderModal'
import { useUserSettings } from '@hooks/settings'
import { gridViewManager } from '@/utils/grid'

const STORAGE_KEY = 'site-bols'

export const SiteBOLs = () => {
  const [showModal, setShowModal] = useState(false)
  const [selectedOrderId, setSelectedOrderId] = useState(null)
  const gridAPIRef = useRef(null)
  const { updateSetting } = useUserSettings()
  const { value: siteBolsDayRange, setValue: settingSiteBolsDayRange } = useLocalStorage(
    'movements-sitebols-day-range',
    {
      back: -3,
      ahead: 1,
    }
  )
  const setSiteBolsDayRange = (newRange) => {
    settingSiteBolsDayRange(newRange)
    updateSetting('movements-sitebols-day-range', newRange)
  }

  const [filters, setFilters] = useState({
    From: moment(subDays(new Date(), siteBolsDayRange.back ? Math.abs(parseInt(siteBolsDayRange.back)) : 3)).startOf(
      'day'
    ),
    To: moment(addDays(new Date(), siteBolsDayRange.ahead ? parseInt(siteBolsDayRange.ahead) : 1)).endOf('day'),
  })
  const reformattedFilters = useMemo(() => {
    const obj = { ...filters }
    if (filters.From && filters.To) {
      obj.From = filters.From.toISOString()
      obj.To = filters.To.toISOString()
    }
    filters['Delivery From'] && (obj['Delivery From'] = moment(filters['Delivery From']).startOf('day').toISOString())
    filters['Delivery To'] && (obj['Delivery To'] = moment(filters['Delivery To']).endOf('day').toISOString())
    return obj
  }, [filters])
  const { useAllocatedBolsQuery } = useOrderMovements()
  const { isFetching, isError, isPending, data } = useAllocatedBolsQuery(reformattedFilters)
  const { scopes } = useSecurityContext()
  const orderReadScope = scopes.scopes.includes('o.r')
  const getDatesFromStorage = () => {
    if (siteBolsDayRange) {
      return {
        From: moment().add(siteBolsDayRange.back, 'days').startOf('day'),
        To: moment().add(siteBolsDayRange.ahead, 'days').endOf('day'),
      }
    }
    return null
  }

  const openOrderModal = (orderId) => {
    setSelectedOrderId(orderId)
    setShowModal(true)
  }
  const closeModal = () => {
    setShowModal(false)
    setSelectedOrderId(null)
  }
  const columnDefs = useMemo(() => getColumnDefs(orderReadScope, openOrderModal), [])
  const getRowId = useCallback((row) => row.data?.id, [])
  const agPropOverrides = useMemo(
    () => ({
      getRowId,
      rowSelection: 'single',
    }),
    []
  )
  return (
    <>
      <GraviGrid
        controlBarProps={{
          title: 'Site BOLs',
          setFilters,
          filters,
          serverParams: data?.params,
          actionButtons: (
            <ActionButtons
              filters={filters}
              setFilters={setFilters}
              gridAPIRef={gridAPIRef}
              getDatesFromStorage={getDatesFromStorage}
              siteBolsDayRange={siteBolsDayRange}
              setSiteBolsDayRange={setSiteBolsDayRange}
            />
          ),
          defaultFilter: { From: subDays(new Date(), 2), To: addDays(new Date(), 2) },
          hiddenFilterKeys: ['from', 'to'],
        }}
        agPropOverrides={agPropOverrides}
        columnDefs={columnDefs}
        externalRef={gridAPIRef}
        loading={isFetching || isPending}
        rowData={data?.rows}
        storageKey={STORAGE_KEY}
        onGridConfigChanged={(config) => updateSetting(`gridConfig::${STORAGE_KEY}`, config)}
        toolPanelWidth={250}
        {...gridViewManager(STORAGE_KEY, updateSetting)}
      />
      <OrderModal selectedOrder={selectedOrderId} showingModal={showModal} closeModal={closeModal} />
    </>
  )
}

const getColumnDefs = (orderReadScope, openOrderModal) => [
  {
    headerName: 'Validation',
    field: 'error',
    key: 'error',
    cellRenderer: (params) =>
      !!params.data?.error ? (
        <Tooltip title={params.data.error}>
          <WarningFilled style={{ color: 'var(--theme-error)' }} />
        </Tooltip>
      ) : null,
    maxWidth: 40,
  },
  {
    headerName: '',
    field: 'id',
    key: 'links',
    cellRenderer: (params) => {
      return (
        <Horizontal gap={8} style={{ overflow: 'visible' }} alignItems={'center'}>
          {orderReadScope && (
            <GraviButton
              size='small'
              icon={<FileTextOutlined />}
              onClick={() => openOrderModal(params?.data?.order_id)}
            />
          )}
          <Link
            target='_blank'
            to={`/order_movements_page/report?${new URLSearchParams({ order_number: params?.data?.order_number })}`}
          >
            <GraviButton size='small' icon={<BookOutlined />} title={'View Order Movements'} />
          </Link>
        </Horizontal>
      )
    },
  },
  {
    headerName: 'Order',
    field: 'order_number',
    key: 'order_number',
    cellRenderer: (params) => <Texto appearance={params?.data?.error && 'error'}> {params?.data?.order_number} </Texto>,
  },
  {
    headerName: 'Purchase Order',
    field: 'po',
    key: 'po',
    hide: true,
  },
  {
    headerName: 'BOL',
    field: 'bol_number',
    key: 'bol_number',
    cellClass: 'stringType',
  },
  {
    headerName: 'Terminal',
    field: 'bol_terminal',
    key: 'bol_terminal',
  },
  {
    headerName: 'Supplier',
    field: 'bol_supplier',
    key: 'bol_supplier',
  },
  {
    headerName: 'Price Type',
    field: 'bol_price_type',
    cellStyle: { textTransform: 'capitalize' },
  },
  {
    headerName: 'BOL Product',
    field: 'bol_product',
    key: 'bol_product',
    hide: true,
  },
  {
    headerName: 'BOL Date',
    field: 'bol_date',
    key: 'bol_date',
    ...defaultDateDef('bol_date'),
  },
  {
    headerName: 'Contract',
    field: 'bol_contract',
    key: 'bol_contract',
  },
  {
    headerName: 'BOL Net Qty',
    field: 'bol_net_volume_original',
    key: 'bol_net_volume_original',
    hide: true,
    ...defaultNumberDef('bol_net_volume_original', 0),
  },
  {
    headerName: 'BOL Gross Qty',
    field: 'bol_gross_volume_original',
    key: 'bol_gross_volume_original',
    hide: true,
    ...defaultNumberDef('bol_gross_volume_original', 0),
  },
  {
    headerName: 'Allocated BOL Net Qty',
    field: 'bol_net_volume_allocated',
    key: 'bol_net_volume_allocated',
    ...defaultNumberDef('bol_net_volume_allocated', 0),
  },
  {
    headerName: 'Allocated BOL Gross Qty',
    field: 'bol_gross_volume_allocated',
    key: 'bol_gross_volume_allocated',
    ...defaultNumberDef('bol_gross_volume_allocated', 0),
  },
  {
    headerName: 'Hauled By',
    field: 'hauled_by',
    key: 'hauled_by',
    hide: true,
  },
  {
    headerName: 'Tractor',
    field: 'tractor',
    hide: true,
    key: 'tractor',
    cellClass: 'stringType',
  },
  {
    headerName: 'Trailer',
    field: 'trailer',
    hide: true,
    key: 'trailer',
    cellClass: 'stringType',
  },

  {
    headerName: 'Site',
    field: 'site',
    key: 'site',
    comparator: (a, b) => a - b,
  },
  {
    headerName: 'Delivered Product',
    field: 'delivery_product',
    key: 'delivery_product',
  },
  {
    headerName: 'Tank',
    field: 'tank_id',
    key: 'tank_id',
  },
  {
    headerName: 'Delivery Date',
    field: 'delivery_date',
    key: 'delivery_date',
    ...defaultDateDef('delivery_date', formatters.DATE, true),
  },

  {
    headerName: 'Delivery Time',
    field: 'delivery_time',
    key: 'delivery_time',
    ...defaultDateDef('delivery_date', formatters.TIME, true),
    filter: false,
    valueGetter: (params) =>
      params?.data?.delivery_date && formatters.TIME(params?.data?.delivery_date, params?.data?.delivery_date_local),
  },
  {
    headerName: 'Drop Qty',
    field: 'delivery_volume',
    key: 'delivery_volume',
    ...defaultNumberDef('delivery_volume'),
    valueFormatter: ({ value }) => value && addCommasToNumber(value, 0),
  },
  {
    headerName: 'Export Date',
    field: 'export_date',
    key: 'export_date',
    ...defaultDateDef('export_date', formatters.DATE_TIME),
    hide: true,
  },
]
